import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiSuccessResponse,
  Comment,
  CommentWithUser,
  CreateCommentRequest,
  CreateCommentResponse,
  EntityType,
  ReactionRequest,
  UpdateCommentRequest,
  UserInfo,
} from '../comments/comments.models';

@Injectable({
  providedIn: 'root',
})
export class CommentsService {
  private baseUrl = environment.fastApiUrl;

  // Cache for user information to avoid repeated API calls
  private userCache = new Map<number, UserInfo>();
  private userCacheLoading = new Map<number, Observable<UserInfo>>();

  // Cache for all users (for mentions autocomplete)
  private allUsersCache: UserInfo[] | null = null;
  private allUsersLoading$: Observable<UserInfo[]> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Get headers with company ID
   */
  private getHeaders(): HttpHeaders {
    const companyId = this.getCompanyId();
    return new HttpHeaders({
      'x-company-id': companyId,
    });
  }

  /**
   * Get company ID from localStorage
   */
  private getCompanyId(): string {
    try {
      const userInfo = JSON.parse(localStorage.getItem('optplanner_user') || '{}');
      return userInfo.company_id || userInfo.companyId || '';
    } catch (error) {
      console.error('Failed to get company ID:', error);
      return '';
    }
  }

  /**
   * Get all comments for a specific entity
   */
  getCommentsForEntity(
    entityType: EntityType,
    entityId: string,
    includeDeleted: boolean = false,
    includeInternal: boolean = true
  ): Observable<CommentWithUser[]> {
    const headers = this.getHeaders();
    const params = new HttpParams()
      .set('entity_type', entityType)
      .set('entity_id', entityId)
      .set('include_deleted', includeDeleted.toString())
      .set('include_internal', includeInternal.toString());

    // Fetch comments; only load users (cached after first call) when there
    // are actually comments to enrich — empty panels skip the users request.
    return this.http.get<Comment[]>(`${this.baseUrl}/api/comments`, { headers, params }).pipe(
      switchMap((comments) => {
        if (comments.length === 0) return of([]);
        return this.getAllUsers().pipe(map(() => comments.map((c) => this.enrichCommentSync(c))));
      })
    );
  }

  /**
   * Get a single comment by ID
   */
  getCommentById(commentId: number): Observable<CommentWithUser> {
    const headers = this.getHeaders();
    return this.http
      .get<Comment>(`${this.baseUrl}/api/comments/${commentId}`, { headers })
      .pipe(map((comment) => this.enrichCommentSync(comment)));
  }

  /**
   * Create a new comment
   */
  createComment(request: CreateCommentRequest): Observable<CreateCommentResponse> {
    const headers = this.getHeaders();
    return this.http.post<CreateCommentResponse>(`${this.baseUrl}/api/comments`, request, {
      headers,
    });
  }

  /**
   * Update an existing comment
   */
  updateComment(commentId: number, request: UpdateCommentRequest): Observable<ApiSuccessResponse> {
    const headers = this.getHeaders();
    return this.http.put<ApiSuccessResponse>(`${this.baseUrl}/api/comments/${commentId}`, request, {
      headers,
    });
  }

  /**
   * Delete a comment (soft delete)
   */
  deleteComment(commentId: number): Observable<ApiSuccessResponse> {
    const headers = this.getHeaders();
    return this.http.delete<ApiSuccessResponse>(`${this.baseUrl}/api/comments/${commentId}`, {
      headers,
    });
  }

  /**
   * Add a reaction to a comment
   */
  addReaction(commentId: number, reactionType: string): Observable<ApiSuccessResponse> {
    const headers = this.getHeaders();
    const request: ReactionRequest = { reaction_type: reactionType as any };
    return this.http.post<ApiSuccessResponse>(
      `${this.baseUrl}/api/comments/${commentId}/reactions`,
      request,
      { headers }
    );
  }

  /**
   * Remove a reaction from a comment
   */
  removeReaction(commentId: number, reactionType: string): Observable<ApiSuccessResponse> {
    const headers = this.getHeaders();
    const params = new HttpParams().set('reaction_type', reactionType);
    return this.http.delete<ApiSuccessResponse>(
      `${this.baseUrl}/api/comments/${commentId}/reactions`,
      { headers, params }
    );
  }

  /**
   * Get comment thread (hierarchical)
   */
  getCommentThread(commentId: number, includeDeleted: boolean = false): Observable<any> {
    const headers = this.getHeaders();
    const params = new HttpParams().set('include_deleted', includeDeleted.toString());
    return this.http.get(`${this.baseUrl}/api/comments/${commentId}/thread`, { headers, params });
  }

  /**
   * Get all active users for mentions autocomplete
   */
  getAllUsers(): Observable<UserInfo[]> {
    // Return cached data if available
    if (this.allUsersCache) {
      return of(this.allUsersCache);
    }

    // Return in-flight request if already loading
    if (this.allUsersLoading$) {
      return this.allUsersLoading$;
    }

    // Fetch all active users
    const headers = this.getHeaders();
    const params = new HttpParams().set('is_active', 'true').set('include_deleted', 'false');

    this.allUsersLoading$ = this.http
      .get<UserInfo[]>(`${this.baseUrl}/auth/users`, { headers, params })
      .pipe(
        tap((users) => {
          this.allUsersCache = users;
          this.allUsersLoading$ = null;
          // Also populate individual user cache
          users.forEach((user) => {
            this.userCache.set(user.id, user);
          });
        }),
        catchError((error) => {
          console.error('Failed to load all users:', error);
          this.allUsersLoading$ = null;
          return of([]);
        })
      );

    return this.allUsersLoading$;
  }

  /**
   * Search users by name for mentions autocomplete
   */
  searchUsers(query: string): Observable<UserInfo[]> {
    return this.getAllUsers().pipe(
      map((users) => {
        if (!query || query.trim() === '') {
          return users.slice(0, 10); // Return first 10 if no query
        }

        const searchTerm = query.toLowerCase();
        return users
          .filter((user) => {
            const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
            const firstName = user.first_name.toLowerCase();
            const lastName = user.last_name.toLowerCase();

            return (
              fullName.includes(searchTerm) ||
              firstName.startsWith(searchTerm) ||
              lastName.startsWith(searchTerm)
            );
          })
          .slice(0, 10); // Limit to 10 results
      })
    );
  }

  /**
   * Get user by ID from cache
   */
  getUserById(userId: number): UserInfo | undefined {
    return this.userCache.get(userId) || this.allUsersCache?.find((u) => u.id === userId);
  }

  /**
   * Get user information by user ID with caching
   */
  getUserInfo(userId: number): Observable<UserInfo> {
    // Check cache first
    if (this.userCache.has(userId)) {
      return of(this.userCache.get(userId)!);
    }

    // Check if already loading
    if (this.userCacheLoading.has(userId)) {
      return this.userCacheLoading.get(userId)!;
    }

    // Fetch from API
    const headers = this.getHeaders();
    const params = new HttpParams().set('user_id', userId.toString());
    const request$ = this.http
      .get<UserInfo>(`${this.baseUrl}/auth/userinfo`, { headers, params })
      .pipe(
        tap((userInfo) => {
          this.userCache.set(userId, userInfo);
          this.userCacheLoading.delete(userId);
        }),
        catchError((error) => {
          console.error(`Failed to load user info for user ${userId}:`, error);
          this.userCacheLoading.delete(userId);
          // Return a placeholder user info
          const placeholder: UserInfo = {
            id: userId,
            first_name: 'Unknown',
            last_name: 'User',
            role_id: 0,
            role_name: '',
            is_super_admin: false,
            profile_pic_url: '',
          };
          return of(placeholder);
        })
      );

    this.userCacheLoading.set(userId, request$);
    return request$;
  }

  /**
   * Enrich a comment synchronously from the already-populated user cache.
   * Call only after getAllUsers() has resolved (guaranteed by getCommentsForEntity).
   */
  private enrichCommentSync(comment: Comment): CommentWithUser {
    const enriched: CommentWithUser = { ...comment };
    enriched.created_by_user = this.userCache.get(comment.created_by);
    if (comment.updated_by) {
      enriched.updated_by_user = this.userCache.get(comment.updated_by);
    }
    return enriched;
  }

  /**
   * Clear user cache (useful for logout/session changes)
   */
  clearUserCache(): void {
    this.userCache.clear();
    this.userCacheLoading.clear();
  }

  /**
   * Get display name for a user
   */
  getUserDisplayName(user: UserInfo | undefined): string {
    if (!user) return 'Unknown User';
    return `${user.first_name} ${user.last_name}`.trim() || 'Unknown User';
  }

  private parseUtcDate(raw: string): Date {
    if (!raw?.trim()) {
      return new Date(NaN);
    }
    const trimmed = raw.trim();
    if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
      return new Date(iso);
    }
    const normalized = trimmed.replace(' ', 'T') + 'Z';
    return new Date(normalized);
  }

  /**
   * Format relative time (e.g., "2 hours ago")
   */
  getRelativeTime(dateString: string): string {
    const date = this.parseUtcDate(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    if (diffMs < 0) {
      return 'just now';
    }
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString();
  }
}
